export const STAGE_KEYS = {
  // UNCONTACTED: 'uncontacted',
  QUOTE_PENDING: 'waiting_for_quotations',
  QUOTATION: 'quotation_available',
  DRAFTING: 'drafting_deliverables',
  DRAFT_READY: 'draft_available',
  PUBLISHING: 'posting_deliverables',
  COMPLETION_REQUEST: 'completion_request',
  CLOSED: 'closed',
  CAMPAIGN_CLOSED: 'campaign_closed',

} as const;

/** Working = Drafting + Draft Ready + Publishing + Completion Request + Closed */
export const WORKING_STAGES = [
  STAGE_KEYS.DRAFTING,
  STAGE_KEYS.DRAFT_READY,
  STAGE_KEYS.PUBLISHING,
  STAGE_KEYS.COMPLETION_REQUEST,
  STAGE_KEYS.CLOSED,
] as const;

/**
 * Stages for Unread filter bubble / list (message unread).
 * Quotation is excluded — those only appear on the Quotation filter.
 */
export const UNREAD_ELIGIBLE_STAGES = [
  // STAGE_KEYS.UNCONTACTED,
  STAGE_KEYS.QUOTE_PENDING,
  STAGE_KEYS.DRAFTING,
  STAGE_KEYS.DRAFT_READY,
  STAGE_KEYS.PUBLISHING,
  STAGE_KEYS.COMPLETION_REQUEST,
  STAGE_KEYS.CLOSED,
] as const;

/** Pending action → stage-filter bubble (1 per person). */
export const ACTION_UNREAD_STAGES = [
  STAGE_KEYS.QUOTE_PENDING,
  STAGE_KEYS.QUOTATION,
  STAGE_KEYS.DRAFT_READY,
  STAGE_KEYS.COMPLETION_REQUEST,
  STAGE_KEYS.CLOSED,
] as const;

/**
 * Actions that also bump the Unread filter chip (1 per person).
 * Quotation stays on Quotation filter only.
 */
export const UNREAD_FILTER_ACTION_STAGES = [
  STAGE_KEYS.QUOTE_PENDING,
  STAGE_KEYS.DRAFT_READY,
  STAGE_KEYS.COMPLETION_REQUEST,
  STAGE_KEYS.PUBLISHING,
  STAGE_KEYS.CLOSED,
] as const;

/** Collapsed row — Quotation is hidden by default (shown when expanded) */
export const TOP_FILTERS = ['working', 'unread', STAGE_KEYS.DRAFTING, STAGE_KEYS.DRAFT_READY] as const;

/**
 * Expanded stage order (below top filters).
 * Unread stages float up; All is always last.
 */
export const STAGE_FILTER_ORDER = [
  STAGE_KEYS.QUOTE_PENDING,
  STAGE_KEYS.QUOTATION,
  STAGE_KEYS.PUBLISHING,
  STAGE_KEYS.COMPLETION_REQUEST,
  STAGE_KEYS.CLOSED,
] as const;

export const ALL_STAGE_KEYS = Object.values(STAGE_KEYS);

/** CometChat `tags` for the active Messages filter (null = no tag filter / All). */
export const getTagsForFilter = (filter: string): string[] | null => {
  if (filter === 'all') return null;
  if (filter === 'working') return [...WORKING_STAGES];
  if (filter === 'unread') return [...UNREAD_ELIGIBLE_STAGES];
  if ((ALL_STAGE_KEYS as readonly string[]).includes(filter)) return [filter];
  return null;
};

/** Pick stage status from CometChat conversation/group tags. */
export const statusFromTags = (tags: unknown): string => {
  if (!Array.isArray(tags)) return '';
  const found = ALL_STAGE_KEYS.find((key) => tags.includes(key));
  return found || (typeof tags[0] === 'string' ? tags[0] : '');
};

export const getStageLabel = (status: string, translations: any) => {
  const map: Record<string, string | undefined> = {
    // [STAGE_KEYS.UNCONTACTED]: translations?.unContacted,
    [STAGE_KEYS.QUOTE_PENDING]: translations?.waitingForQuotations,
    [STAGE_KEYS.QUOTATION]: translations?.quotationAvailable,
    [STAGE_KEYS.DRAFTING]: translations?.draftDeliverables,
    [STAGE_KEYS.DRAFT_READY]: translations?.draftAvailable,
    [STAGE_KEYS.PUBLISHING]: translations?.postingDeliverables,
    [STAGE_KEYS.COMPLETION_REQUEST]: translations?.completionRequest,
    [STAGE_KEYS.CLOSED]: translations?.closed,
    [STAGE_KEYS.CAMPAIGN_CLOSED]: "Campaign Completed",
    working: translations?.working,
    unread: translations?.unread,
    all: translations?.all,
  };
  return map[status] ?? status;
};

export const getStageColorClass = (status: string) => {
  const map: Record<string, string> = {
    // [STAGE_KEYS.UNCONTACTED]: 'text-unContacted bg-unContacted/10',
    [STAGE_KEYS.QUOTE_PENDING]: 'text-waitingQuote bg-waitingQuote/10',
    [STAGE_KEYS.QUOTATION]: 'text-quoteAvailable bg-quoteAvailable/10',
    [STAGE_KEYS.DRAFTING]: 'text-draftDeliverables bg-draftDeliverables/10',
    [STAGE_KEYS.DRAFT_READY]: 'text-draftAvailable bg-draftAvailable/10',
    [STAGE_KEYS.PUBLISHING]: 'text-postingDeliverables bg-postingDeliverables/10',
    [STAGE_KEYS.COMPLETION_REQUEST]: 'text-completionRequest bg-completionRequest/10',
    [STAGE_KEYS.CLOSED]: 'text-closed bg-closed/10',
  };
  return map[status] ?? 'text-neutral-600 bg-neutral-100';
};

/** Filters that never show a purple unread bubble */
export const FILTERS_WITHOUT_UNREAD_BADGE = new Set(['working', 'all']);

/** Display unread as 1–99, or 99+ when higher. */
export const formatUnreadBadgeCount = (count: number): string => {
  const n = Number(count) || 0;
  if (n <= 0) return '';
  return n > 99 ? '99+' : String(n);
};

/**
 * Infer stage from a CometChat custom/status message (draft / quotation / completion).
 * Used to keep list badges attributed to the right filter when tags are not yet refreshed.
 */
export const inferStageFromChatMessage = (message: any): string | null => {
  if (!message) return null;
  const type = typeof message.getType === 'function' ? message.getType() : message.type;
  const customData =
    (typeof message.getCustomData === 'function' ? message.getCustomData() : null) ||
    message?.data?.customData ||
    {};

  if (customData?.status && (ALL_STAGE_KEYS as readonly string[]).includes(String(customData.status))) {
    return String(customData.status);
  }

  // Job-offer / quotation card
  if (type === 'custom' && (customData?.title || customData?.amount || customData?.link)) {
    return STAGE_KEYS.QUOTATION;
  }

  const blob = [
    customData?.brandMessage,
    customData?.influencerMessage,
    customData?.adminMessage,
    customData?.text,
    message?.data?.text,
    typeof message.getText === 'function' ? message.getText() : message?.text,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  // Brand's own action confirmations — not a new inbound request
  if (/^you have\b/.test(blob.trim()) || /\byou have (approved|rejected|chased|archived|terminated)\b/.test(blob)) {
    return null;
  }
  
  if (/submitted a new draft|has submitted.*draft|new draft/.test(blob)) {
    return STAGE_KEYS.DRAFT_READY;
  }
  if (/requested job completion|job completion|completion request/.test(blob)) {
    return STAGE_KEYS.COMPLETION_REQUEST;
  }
  if (/quotation request|submitted.*quotation|has applied|waiting for quotation/.test(blob)) {
    return STAGE_KEYS.QUOTATION;
  }
  if (/withdrawn from (the )?campaign|has withdrawn|creator has withdrawn/.test(blob)) {
    return STAGE_KEYS.CLOSED;
  }

  return null;
};

/** Brand-initiated action/status copy — never counts as unread for the brand. */
export const isBrandOwnedActionMessage = (message: any): boolean => {
  if (!message) return false;
  const customData =
    (typeof message.getCustomData === 'function' ? message.getCustomData() : null) ||
    message?.data?.customData ||
    {};
  const blob = [
    customData?.brandMessage,
    customData?.text,
    message?.data?.text,
    typeof message.getText === 'function' ? message.getText() : message?.text,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .trim();

  if (!blob) return false;
  if (/^you have\b/.test(blob)) return true;
  if (/\byou have (approved|rejected|chased|archived|terminated)\b/.test(blob)) return true;
  if (/^you chased\b/.test(blob)) return true;
  return false;
};

/**
 * CometChat group create / member added / joined / left / kicked / scope changed.
 * Unread is handled by CometChat setting `core.conversations.updateOnGroupActions=false`.
 * Keep this only so live listeners never treat lifecycle events as chat unread/preview bumps.
 */
export const isCometGroupLifecycleMessage = (message: any): boolean => {
  if (!message) return false;

  const category = String(
    (typeof message.getCategory === 'function' ? message.getCategory() : null) ||
      message.category ||
      message?.data?.category ||
      ''
  ).toLowerCase();
  if (category === 'action') return true;

  const type = String(
    (typeof message.getType === 'function' ? message.getType() : message.type) || ''
  ).toLowerCase();
  if (
    type === 'groupmember' ||
    type === 'added' ||
    type === 'joined' ||
    type === 'left' ||
    type === 'kicked' ||
    type === 'banned' ||
    type === 'unbanned' ||
    type === 'scopechanged'
  ) {
    return true;
  }

  const action = String(
    message?.action || message?.data?.action || message?.data?.entities?.action || ''
  ).toLowerCase();
  if (
    /^(groupmember|added|joined|left|kicked|banned|unbanned|scopechanged)$/.test(action) ||
    /member.*(added|joined|left|kicked)/.test(action)
  ) {
    return true;
  }

  return false;
};

/** Text / media / custom / status — each inbound message counts +1 on the conversation row. */
export const shouldCountMessageAsBrandUnread = (message: any): boolean => {
  if (!message) return false;
  if (isCometGroupLifecycleMessage(message)) return false;
  if (isBrandOwnedActionMessage(message)) return false;
  const type = typeof message.getType === 'function' ? message.getType() : message.type;
  if (type === 'text' || type === 'image' || type === 'video' || type === 'audio' || type === 'file') {
    return true;
  }
  // Videos are uploaded as custom `video_upload` (not CometChat media type)
  if (type === 'video_upload') return true;
  const customData =
    (typeof message.getCustomData === 'function' ? message.getCustomData() : null) ||
    message?.data?.customData ||
    {};
  if (customData?.fileUrl && (customData?.fileType || customData?.fileName)) return true;
  // Job-offer card (`custom`) and apply/status bubbles — count on the row.
  // Unread filter still excludes Quotation via conversationCountsTowardUnreadFilter.
  if (type === 'custom' || type === 'status') return true;

  // Draft / completion / withdraw copy when type is not custom/status
  const stage = inferStageFromChatMessage(message);
  if (stage) return true;

  // Other status / action copy (delete draft, publish post, etc.)
  const actionBlob = [
    customData?.brandMessage,
    customData?.influencerMessage,
    customData?.adminMessage,
    customData?.text,
    message?.data?.text,
    typeof message.getText === 'function' ? message.getText() : message?.text,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (!actionBlob.trim()) return false;
  // if (/quotation request|submitted.*quotation|has applied|waiting for quotation/.test(actionBlob)) {
  //   return false;
  // }
  if (type === 'status') return true;
  if (
    /deleted a draft|has deleted.*draft|have deleted a draft|draft.*(deleted|removed)/.test(actionBlob) ||
    /published a new post|have published|deliverable.*(published|posted)/.test(actionBlob) ||
    /withdrawn from (the )?campaign|has withdrawn|creator has withdrawn/.test(actionBlob) ||
    /quotation request|submitted.*quotation|has applied|waiting for quotation/.test(actionBlob) ||
    /updated budget price|budget price.*updated/.test(actionBlob) ||
    /has created dispute/.test(actionBlob)
  ) {
    return true;
  }
  return false;
};

/** Draft / quotation / completion action → stage filter badge (1 per person). Brand actions excluded. */
export const isActionUnreadEvent = (message: any): boolean => {
  if (isCometGroupLifecycleMessage(message)) return false;
  if (isBrandOwnedActionMessage(message)) return false;
  const stage = inferStageFromChatMessage(message);
  return !!(stage && (ACTION_UNREAD_STAGES as readonly string[]).includes(stage));
};

/** List-row last-message preview (text, media label, or custom/status copy). */
export const getConversationMessagePreview = (message: any): string => {
  if (!message) return '';
  const type = typeof message.getType === 'function' ? message.getType() : message?.type;
  if (type === 'image') return 'Photo';
  if (type === 'video' || type === 'video_upload') return 'Video';
  if (type === 'audio') return 'Audio';
  if (type === 'file') return 'File';

  const customData =
    (typeof message.getCustomData === 'function' ? message.getCustomData() : null) ||
    message?.data?.customData ||
    {};

  // Status / chase / approve copy — brandMessage first (list preview for brand)
  const statusPreview =
    customData?.brandMessage ||
    customData?.influencerMessage ||
    customData?.adminMessage ||
    '';
  if ((type === 'status' || type === 'custom') && typeof statusPreview === 'string' && statusPreview.trim()) {
    // Job-offer cards also use type custom with title/amount — don't steal those
    if (!(customData?.title || customData?.amount || customData?.link || message?.data?.amount)) {
      return statusPreview.trim();
    }
  }

  // Job offer card (custom) — title may live on customData or data root; text is often ""
  const jobOfferTitle =
    customData?.title ||
    message?.data?.title ||
    '';
  if (
    (type === 'custom' || customData?.amount || customData?.due || customData?.link || message?.data?.amount) &&
    jobOfferTitle
  ) {
    return String(jobOfferTitle).trim();
  }
  if (type === 'custom' && (customData?.amount || customData?.link || message?.data?.amount || message?.data?.link)) {
    return 'Job Offer';
  }

  // S3 / custom file attachments (used for video uploads)
  const fileType = String(customData?.fileType || '');
  if (customData?.fileUrl || customData?.fileName) {
    if (/^image\//i.test(fileType) || /\.(png|jpe?g|gif|webp|svg)$/i.test(String(customData?.fileName || ''))) {
      return 'Photo';
    }
    if (/^video\//i.test(fileType) || type === 'video_upload') return 'Video';
    if (/^audio\//i.test(fileType)) return 'Audio';
    return 'File';
  }

  // CometChat media REST/SDK payload without a clean type string
  const dataUrl = message?.data?.url || message?.data?.file?.url;
  const dataResource = String(message?.data?.resource || message?.data?.name || '');
  const dataMime = String(message?.data?.type || message?.data?.mimeType || '');
  if (dataUrl || message?.data?.attachments?.length) {
    if (/^image\//i.test(dataMime) || /image/i.test(String(type || '')) || /\.(png|jpe?g|gif|webp|svg)$/i.test(dataResource)) {
      return 'Photo';
    }
    if (/^video\//i.test(dataMime) || /video/i.test(String(type || ''))) return 'Video';
    if (/^audio\//i.test(dataMime) || /audio/i.test(String(type || ''))) return 'Audio';
    if (dataUrl) return 'File';
  }

  const text =
    customData?.brandMessage ||
    customData?.influencerMessage ||
    (typeof customData?.text === 'string' && customData.text.trim() ? customData.text : '') ||
    (typeof message?.data?.text === 'string' && message.data.text.trim() ? message.data.text : '') ||
    (typeof message.getText === 'function' ? message.getText() : message?.text) ||
    jobOfferTitle ||
    '';

  if (typeof text === 'string' && text.trim()) return text.trim();

  const attachments = message?.data?.attachments || message?.attachments || [];
  if (Array.isArray(attachments) && attachments.length) {
    const mime = String(attachments[0]?.mimeType || attachments[0]?.extension || '');
    if (/image/i.test(mime)) return 'Photo';
    if (/video/i.test(mime)) return 'Video';
    if (/audio/i.test(mime)) return 'Audio';
    return 'File';
  }

  const restType =
    message?.type ||
    message?.data?.type ||
    message?.data?.attachments?.[0]?.mimeType ||
    '';
  if (/image/i.test(String(restType))) return 'Photo';
  if (/video/i.test(String(restType))) return 'Video';
  if (/audio/i.test(String(restType))) return 'Audio';
  if (/file|application|pdf/i.test(String(restType))) return 'File';

  return '';
};

/**
 * Whether this conversation belongs in the Unread filter (list + purple bubble).
 *
 * Include: unread in every stage except Quotation (text / media / action).
 * Exclude: Quotation status (any unread) → Quotation filter box only.
 */
export const conversationCountsTowardUnreadFilter = (
  item: any,
  unreadCounts: Record<string, number> = {}
): boolean => {
  const action = item?.actionUnreadStage;
  const status = item?.status;

  // Quotation stage — never on Unread filter (only Quotation filter box)
  if (status === STAGE_KEYS.QUOTATION || action === STAGE_KEYS.QUOTATION) {
    return false;
  }

  // Draft Ready / Completion action → Unread + stage filter
  if (action && (UNREAD_FILTER_ACTION_STAGES as readonly string[]).includes(action)) {
    return true;
  }

  if (getItemUnread(item, unreadCounts) <= 0) return false;
  if (!status) return false;
  return (UNREAD_ELIGIBLE_STAGES as readonly string[]).includes(status);
};

/**
 * Expanded order:
 * a) Working/Unread/Drafting/Draft Ready on top
 * b) stages with unread people float above others
 * c) All always last
 */
export const orderExpandedFilters = (
  unreadPeopleByStatus: Record<string, number>
): string[] => {
  const topSet = new Set(TOP_FILTERS as readonly string[]);
  const withUnread = STAGE_FILTER_ORDER.filter(
    (s) => (unreadPeopleByStatus[s] || 0) > 0 && !topSet.has(s)
  );
  const withoutUnread = STAGE_FILTER_ORDER.filter(
    (s) => !(unreadPeopleByStatus[s] > 0) && !topSet.has(s)
  );
  return [...TOP_FILTERS, ...withUnread, ...withoutUnread, 'all'];
};

export const flattenCampaignInfluencers = (listData: any) => {
  if (!listData) return [];
  const byStatus =
    listData?.data && typeof listData.data === 'object' && !Array.isArray(listData.data)
      ? listData.data
      : listData;
  const items: any[] = [];
  ALL_STAGE_KEYS.forEach((status) => {
    const bucket = byStatus?.[status];
    const influencers = Array.isArray(bucket?.campaignInfluencers)
      ? bucket.campaignInfluencers
      : Array.isArray(bucket)
        ? bucket
        : [];
    influencers.forEach((item: any) => {
      items.push({ ...item, status: item?.status || item?.influencer_status || status });
    });
  });
  return items;
};

/** Preview text that means the brand (logged-in user) just acted — treat as read. */
export const looksLikeBrandActionPreview = (text?: string) => {
  const t = String(text || '')
    .trim()
    .toLowerCase();
  if (!t) return false;
  return /^you have\b/.test(t) || /^you chased\b/.test(t);
};

/** Unread for one conversation: Redux + API unreadMessageCount */
export const getItemUnread = (
  item: any,
  unreadCounts: Record<string, number> = {}
) => {
  // Brand's own action as last message → never show unread
  if (looksLikeBrandActionPreview(item?.message)) return 0;
  const id = item?.id;
  if (!id) return Number(item?.unreadMessageCount || 0);
  return Math.max(
    Number(unreadCounts[id] || 0),
    Number(unreadCounts[`group_${id}`] || 0),
    Number(item?.unreadMessageCount || 0)
  );
};

/** List-row badge: exact unread total (text + media + custom/status messages). */
export const getConversationListBadge = (
  item: any,
  unreadCounts: Record<string, number> = {}
) => {
  if (looksLikeBrandActionPreview(item?.message)) return 0;
  return getItemUnread(item, unreadCounts);
};

/** Unread filter purple bubble: unique people that belong in Unread (not quotation submits). */
export const countPeopleWithUnread = (
  conversations: any[],
  unreadCounts: Record<string, number>,
  _stageFilter?: readonly string[] | null
) => {
  return conversations.reduce((sum, item) => {
    if (!conversationCountsTowardUnreadFilter(item, unreadCounts)) return sum;
    return sum + 1;
  }, 0);
};

/**
 * Per-stage filter badges = unique people with unread in that stage.
 * Draft Ready / Quotation / Completion: action submit = 1 per person.
 */
export const countPeopleWithUnreadByStatus = (
  conversations: any[],
  unreadCounts: Record<string, number>
) => {
  const map: Record<string, number> = {};
  conversations.forEach((item) => {
    const status =
      item?.status ||
      (Array.isArray(item?.tags)
        ? item.tags.find((t: string) => (ALL_STAGE_KEYS as readonly string[]).includes(t))
        : '');

    const actionStage = item?.actionUnreadStage;
    if (actionStage && (ACTION_UNREAD_STAGES as readonly string[]).includes(actionStage)) {
      map[actionStage] = (map[actionStage] || 0) + 1;
      return;
    }

    if (!status || getItemUnread(item, unreadCounts) <= 0) return;
    map[status] = (map[status] || 0) + 1;
  });
  return map;
};

/** Campaign strip badge — same rules as Unread filter (excludes quotation submits). */
export const countCampaignUnreadPeople = (
  conversations: any[],
  unreadCounts: Record<string, number>,
  campaignId?: string | number,
  _stageFilter?: readonly string[] | null
) => {
  if (campaignId == null || campaignId === '') return 0;
  const id = String(campaignId);
  return conversations.reduce((sum, item) => {
    const match =
      String(item?.campaign?.id || '') === id ||
      (Array.isArray(item?.tags) && item.tags.includes(id));
    if (!match) return sum;
    if (!conversationCountsTowardUnreadFilter(item, unreadCounts)) return sum;
    return sum + 1;
  }, 0);
};
